"use client"

import { useState, useEffect, useCallback } from "react"
import { fetchDevices, fetchAlerts } from "../utils/api"

export const useRealTimeData = () => {
  const [devices, setDevices] = useState([])
  const [alerts, setAlerts] = useState([])
  const [lastUpdate, setLastUpdate] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const [devicesData, alertsData] = await Promise.all([fetchDevices(), fetchAlerts()])

      setDevices(devicesData)
      setAlerts(alertsData)
      setLastUpdate(new Date().toISOString())
    } catch (error) {
      console.error("Error loading data:", error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()

    // Set up auto-refresh every 5 seconds
    const interval = setInterval(loadData, 5000)

    return () => clearInterval(interval)
  }, [loadData])

  const acknowledgeAlert = useCallback((alertId) => {
    setAlerts((prev) => prev.map((alert) => (alert.id === alertId ? { ...alert, acknowledged: true } : alert)))
  }, [])

  const addDevice = useCallback((newDevice) => {
    setDevices((prev) => [...prev, newDevice])
  }, [])

  const removeDevice = useCallback((deviceId) => {
    setDevices((prev) => prev.filter((device) => device.id !== deviceId))
    setAlerts((prev) => prev.filter((alert) => alert.deviceId !== deviceId))
  }, [])

  const updateThresholds = useCallback((deviceId, thresholds) => {
    setDevices((prev) =>
      prev.map((device) =>
        device.id === deviceId
          ? {
              ...device,
              sensors: {
                ...device.sensors,
                chemical: {
                  ...device.sensors.chemical,
                  threshold: thresholds.chemical || device.sensors.chemical.threshold,
                },
                vibration: {
                  ...device.sensors.vibration,
                  threshold: thresholds.vibration || device.sensors.vibration.threshold,
                },
              },
            }
          : device,
      ),
    )
  }, [])

  const exportLogs = useCallback(() => {
    const csvData = [
      ["Timestamp", "Device ID", "Alert Type", "Severity", "Message", "Location", "Acknowledged"],
      ...alerts.map((alert) => [
        alert.timestamp,
        alert.deviceId,
        alert.type,
        alert.severity,
        alert.message,
        alert.location.address,
        alert.acknowledged ? "Yes" : "No",
      ]),
    ]

    const csvContent = csvData.map((row) => row.join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `surakshanet-logs-${new Date().toISOString().split("T")[0]}.csv`
    link.click()
    window.URL.revokeObjectURL(url)
  }, [alerts])

  return {
    devices,
    alerts,
    lastUpdate,
    isLoading,
    acknowledgeAlert,
    addDevice,
    removeDevice,
    updateThresholds,
    exportLogs,
  }
}

